import LoadingButton from "@/components/LoadingButton/LoadingButton";
import { VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useReservation, { reservationHasPassed } from "@/hooks/useReservation";
import useVenue from "@/hooks/useVenue";
import { ButtonProps } from "@mui/material";
import Stack from "@mui/material/Stack";
import React, { useMemo } from "react";
import ResetReservationButton from "./ResetReservationButton";

type EditReservationStatusButtonsProps = {
  disabled?: boolean;
  reservation: VenueReservation;
  venuePortalUserIdToResetTo?: string;
  AcceptButton?: React.ComponentType<ButtonProps>;
};
export default function EditReservationStatusButtons({
  disabled,
  AcceptButton,
  reservation,
  venuePortalUserIdToResetTo,
}: EditReservationStatusButtonsProps) {
  const {
    acceptReservation,
    rejectReservation,
    editVenueReservationAnswerMutationResponse: { loading },
  } = useReservation();

  const sharedButtonProps = useMemo(
    () => ({
      variant: "contained" as const,
      fullWidth: true,
      sx: { height: "100%", fontSize: 22 },
      disabled: disabled || loading,
    }),
    [disabled, loading],
  );

  const { productMap } = useVenue();
  const defaultVenueId = useMemo(
    () =>
      productMap.get(reservation.group?.product?.id ?? "")?.defaultVenue?.id,
    [reservation.group, productMap],
  );

  const { t } = useLocalization();

  return (
    <Stack
      sx={{ flex: 1, height: 64 }}
      alignItems="center"
      data-testid="edit-reservation-status-button"
    >
      {reservation?.answer || reservationHasPassed(reservation) ? (
        <ResetReservationButton
          reservation={reservation}
          disabled={disabled || loading}
          venuePortalUserIdToResetTo={venuePortalUserIdToResetTo}
          buttonSx={{
            fontSize: 22,
          }}
        />
      ) : (
        <Stack direction="row" spacing={1} width="100%" height="100%">
          {AcceptButton ? (
            <AcceptButton color="success" {...sharedButtonProps} />
          ) : (
            <LoadingButton
              color="success"
              onClick={async () =>
                await acceptReservation(reservation.id, defaultVenueId)
              }
              aria-label={t("command:acceptWhat", {
                interpolation: { what: t("nouns:reservation") },
              })}
              {...sharedButtonProps}
            >
              {t("command:accept")}
            </LoadingButton>
          )}
          <LoadingButton
            color="error"
            onClick={async () => await rejectReservation(reservation.id)}
            aria-label={t("command:rejectWhat", {
              interpolation: { what: t("nouns:reservation") },
            })}
            {...sharedButtonProps}
          >
            {t("command:reject")}
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}
